<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <HelpText>
            <h3>
              Edit Channel
            </h3>

            <p>
              Edit the details of the channel.
            </p>
          </HelpText>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <form @submit.prevent="submit" class="form-horizontal">
                <TextFormGroup
                    v-model="data.name"
                    name="name"
                    label="Price"
                    help-text="The name of the publication based channel."
                    :error="getError('name')"
                />

                <ColorFormGroup
                    v-model="data.color"
                    name="color"
                    label="Color"
                    help-text="The color of the channel."
                    :colors="colors.data"
                    :error="getError('color')"
                />

                <SelectFormGroup
                    v-model="data.accounting_system_product_number"
                    name="accounting_system_product_number"
                    label="Accounting System Product"
                    help-text="The product in the accounting system that the channel belongs to."
                    :error="getError('accounting_system_product_number')"
                    :options="bookkeepingProductNumberOptions"
                />

                <SelectFormGroup
                    v-model="data.channel_type"
                    name="channel_type"
                    label="Channel Type"
                    help-text="The channel type of the channel."
                    :error="getError('channel_type')"
                    :options="channelTypeOptions"
                    :nullable="true"
                />

                <NumberFormGroup
                    v-model="data.default_content_deadline_period"
                    name="default_content_deadline_period"
                    label="Default Content Deadline"
                    help-text="The default number of days before publication of the ad that the content should be submitted."
                    :error="getError('default_content_deadline_period')"
                />

                <NumberFormGroup
                    v-model="data.default_content_deadline_reminder_period"
                    name="default_content_deadline_reminder_period"
                    label="Default Content Deadline Reminder"
                    help-text="The default number of days before the content deadline of the ad that a reminder should be sent to the content responsible."
                    :error="getError('default_content_deadline_period')"
                />

                <hr>

                <SubmitFormGroup button-class="btn-black"/>
              </form>
            </div>
          </div>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <HelpText>
            <h3>
              Publications
            </h3>

            <p>
              These are the publications that have been made in this channel.
            </p>

            <div class="btn-group">
              <button type="button" class="btn btn-black btn-sm dropdown-toggle" data-toggle="dropdown">
                <i class="fa fa-cogs"></i> Actions <span class="caret"></span>
              </button>

              <ul class="dropdown-menu">
                <li>
                  <router-link
                      :to="'/portals/' + portal.id + '/publication-based-channels/' + publicationBasedChannelId + '/publications/create'">
                    <i class="fa fa-plus"></i> New Publication
                  </router-link>
                </li>

                <li>
                  <a href="#import-publications-modal" data-toggle="modal">
                    <i class="fa fa-upload"></i> Import Publications
                  </a>
                </li>
              </ul>
            </div>
          </HelpText>
        </div>

        <div class="col-xs-18">
          <PublicationListPanel :publication-based-channel="publicationBasedChannel"/>
        </div>
      </div>
      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <HelpText>
            <h3>
              Ad Formats
            </h3>

            <p>
              These are the subchannels under this channel.
            </p>

            <router-link
                class="btn btn-black btn-sm"
                :to="'/portals/'+ portal.id + '/publication-based-channels/' + publicationBasedChannel.id + '/ad-formats/create'"
            >
              <i class="fa fa-plus"></i> New
            </router-link>
          </HelpText>
        </div>

        <div class="col-xs-18">
          <PublicationBasedAdFormatListPanel :portal="portal" :publication-based-channel="publicationBasedChannel"/>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <HelpText>
            <h3>
              Ad Locations
            </h3>

            <p>
              These are the ad locations that are available in this channel.
            </p>

            <a
                href="#create-publication-based-ad-location-modal"
                class="btn btn-black btn-sm"
                data-toggle="modal"
            >
              <i class="fa fa-plus"></i> New
            </a>
          </HelpText>
        </div>

        <div class="col-xs-18">
          <PublicationBasedAdLocationListPanel :publication-based-channel="publicationBasedChannel"/>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Budget
            </h3>

            <p>
              This table displays the budget for the channel.
            </p>
          </help-text>
        </div>

        <div class="col-xs-18">
          <NewList
              label="Publication"
              :period-width="7"
              :targets="[{name: 'base', label: 'Base', width: 7}, {name: 'management', label: 'Management', width: 7}]"
              :endpoint="'/api/v2/publication-channels/' + publicationBasedChannel.id + '/targets'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import ColorFormGroup from '@/components/FormGroups/Horizontal/Color'
import Errors from '@/mixins/Errors'
import HelpText from '@/components/HelpText'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import NewList from '../../../../../components/Panels/Administration/Targets/NewList'
import NumberFormGroup from '@/vendor/forms/FormGroups/Horizontal/Number'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import PublicationListPanel from '@/components/Administration/PublicationBasedChannelPublications/Panels/List'
import PublicationBasedAdFormatListPanel from '@/components/Administration/PublicationBasedAdFormats/Panels/List'
import PublicationBasedAdLocationListPanel from '@/components/Administration/PublicationBasedAdLocations/Panels/List'

export default {
  components: {
    ColorFormGroup,
    HelpText,
    LoadingContainer,
    NewList,
    NumberFormGroup,
    PublicationListPanel,
    SelectFormGroup,
    SubmitFormGroup,
    TextFormGroup,
    PublicationBasedAdFormatListPanel,
    PublicationBasedAdLocationListPanel
  },

  mixins: [Errors],

  model: {
    prop: 'portal',
    event: 'portal-updated'
  },

  props: ['portal', 'publicationBasedChannelId'],

  data() {
    return {
      data: {},

      publicationBasedChannel: null,
      bookkeepingProducts: null,
      channelTypes: null,
      colors: null,

      loading: true
    }
  },

  mounted() {
    this.load()
  },

  computed: {
    bookkeepingProductNumberOptions() {
      return this.bookkeepingProducts.map(bookkeepingProduct => {
        return {
          value: bookkeepingProduct.id,
          label: bookkeepingProduct.attributes.name
        }
      })
    },

    channelTypeOptions() {
      return this.channelTypes.map(channelType => {
        return {
          value: channelType.id,
          label: channelType.attributes.name
        }
      })
    }
  },

  methods: {
    load() {
      Promise.all([
        http.get('/api/v2/publication-based-channels/' + this.publicationBasedChannelId),
        http.get('/api/v2/bookkeeping-products'),
        http.get('/api/v2/colors'),
        http.get('/api/v2/channel-types'),
      ]).then(responses => {
        this.publicationBasedChannel = responses[0].data.data

        this.data = {...this.publicationBasedChannel.attributes}

        this.bookkeepingProducts = responses[1].data.data

        this.colors = responses[2].data.data

        this.channelTypes = responses[3].data.data

        this.loading = false
      })
    },

    submit() {
      http.put('/api/v2/publication-based-channels/' + this.publicationBasedChannel.id, this.data)
          .then(() => {
            notify.success('The publication based channel was saved')

            this.errors = {}
          })
          .catch(this.catchValidationErrors)
    }
  }
}
</script>
